import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { adminService, postService, AdminUser, BannedUser, Post, contactService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Shield,
  Users,
  FileX,
  Ban,
  UserX,
  Search,
  Mail,
  UserCheck,
  UserMinus,
  Trash2,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { format, addDays } from "date-fns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"users" | "posts" | "banned" | "contact">("users");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_users: 0,
    active_posts: 0,
    banned_users: 0,
    total_posts: 0,
  });
  const [contactMessages, setContactMessages] = useState<any[]>([]);
  const [contactSearch, setContactSearch] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyTargetId, setReplyTargetId] = useState<string | null>(null);
  const [replySendingId, setReplySendingId] = useState<string | null>(null);
  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const [deleteContactTargetId, setDeleteContactTargetId] = useState<string | null>(null);
  const [deleteContactLoading, setDeleteContactLoading] = useState(false);

  // Search and filters
  const [userSearch, setUserSearch] = useState("");
  const [userLevelFilter, setUserLevelFilter] = useState("");
  const [postSearch, setPostSearch] = useState("");

  // Modal states
  const [showBanModal, setShowBanModal] = useState(false);
  const [showDeletePostModal, setShowDeletePostModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  // Form states
  const [banType, setBanType] = useState<"temporary" | "permanent">("temporary");
  const [banReason, setBanReason] = useState("");
  const [banDuration, setBanDuration] = useState(1); // days
  const [deleteReason, setDeleteReason] = useState("");
  const [notificationSubject, setNotificationSubject] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");

  useEffect(() => {
    if (user?.level === "admin") {
      loadInitialData();
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "users") {
      loadUsers();
    } else if (activeTab === "posts") {
      loadPosts();
    } else if (activeTab === "banned") {
      loadBannedUsers();
    } else if (activeTab === "contact") {
      loadContactMessages();
    }
  }, [activeTab, userSearch, userLevelFilter, postSearch, contactSearch]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadUsers(), loadPosts(), loadBannedUsers(), loadStats()]);
    } catch (error) {
      toast.error("Error loading admin panel data");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await adminService.getStats();
      setStats(data);
    } catch (error) {
      console.error("Error loading stats", error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await adminService.getUsers(0, 50, userLevelFilter || undefined, userSearch || undefined);
      setUsers(response.users);
    } catch (error) {
      toast.error("Error loading users");
    }
  };

  const loadPosts = async () => {
    try {
      const allPosts = await postService.getPosts(0, 50);
      // Filter posts based on search if needed
      const filteredPosts = postSearch
        ? allPosts.filter(
            (post) =>
              post.title.toLowerCase().includes(postSearch.toLowerCase()) ||
              post.author_username.toLowerCase().includes(postSearch.toLowerCase())
          )
        : allPosts;
      setPosts(filteredPosts);
    } catch (error) {
      toast.error("Error loading posts");
    }
  };

  const loadBannedUsers = async () => {
    try {
      const response = await adminService.getBannedUsers(0, 50);
      setBannedUsers(response.banned_users);
    } catch (error) {
      toast.error("Error loading banned users");
    }
  };

  const loadContactMessages = async () => {
    try {
      const res = await contactService.listMessages(0, 50);
      const msgs = res.messages || [];
      const filtered = contactSearch
        ? msgs.filter(
            (m: any) =>
              (m.subject || "").toLowerCase().includes(contactSearch.toLowerCase()) ||
              (m.sender_email || "").toLowerCase().includes(contactSearch.toLowerCase()) ||
              (m.sender_username || "").toLowerCase().includes(contactSearch.toLowerCase()) ||
              (m.message || "").toLowerCase().includes(contactSearch.toLowerCase())
          )
        : msgs;
      setContactMessages(filtered);
    } catch (error) {
      toast.error("Error loading contact messages");
    }
  };

  const handlePromoteUser = async (userId: string, newLevel: "user" | "admin") => {
    try {
      await adminService.updateUserLevel({ user_id: userId, level: newLevel });
      const action = newLevel === "admin" ? "promoted to admin" : "demoted to user";
      toast.success(`User successfully ${action}`);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error updating user level");
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser || !banReason.trim()) {
      toast.error("Please complete all fields");
      return;
    }

    try {
      const banData: any = {
        user_id: selectedUser.id,
        ban_type: banType,
        ban_reason: banReason.trim(),
      };

      if (banType === "temporary") {
        const banUntil = addDays(new Date(), banDuration);
        banData.ban_until = banUntil.toISOString();
      }

      await adminService.banUser(banData);

      const banMessage = banType === "permanent" ? "permanently banned" : `banned for ${banDuration} days`;

      toast.success(`User ${banMessage} successfully`);

      // Reset form and close modal
      setShowBanModal(false);
      setBanReason("");
      setBanType("temporary");
      setBanDuration(1);
      setSelectedUser(null);

      // Reload data
      loadUsers();
      loadBannedUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error banning user");
    }
  };

  const handleUnbanUser = async (userId: string) => {
    try {
      await adminService.unbanUser(userId);
      toast.success("User unbanned successfully");
      loadBannedUsers();
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error unbanning user");
    }
  };

  const handleDeletePost = async () => {
    if (!selectedPost || !deleteReason.trim()) {
      toast.error("Please enter a reason for deleting the post");
      return;
    }

    try {
      await adminService.deletePost(selectedPost.id, deleteReason.trim());
      toast.success("Post deleted successfully");

      // Reset form and close modal
      setShowDeletePostModal(false);
      setDeleteReason("");
      setSelectedPost(null);

      // Reload posts
      loadPosts();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error deleting post");
    }
  };

  const handleSendNotification = async () => {
    if (!selectedUser || !notificationSubject.trim() || !notificationMessage.trim()) {
      toast.error("Please complete all fields");
      return;
    }

    try {
      await adminService.sendNotification({
        user_id: selectedUser.id,
        subject: notificationSubject.trim(),
        message: notificationMessage.trim(),
        notification_type: "general",
      });

      toast.success("Notification sent successfully");

      // Reset form and close modal
      setShowNotificationModal(false);
      setNotificationSubject("");
      setNotificationMessage("");
      setSelectedUser(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error sending notification");
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastMessage.trim()) {
      toast.error("Please complete subject and message");
      return;
    }
    try {
      const res = await adminService.sendBroadcast({
        subject: broadcastSubject.trim(),
        message: broadcastMessage.trim(),
        notification_type: "general",
        target_levels: ["user", "admin"],
        include_banned: false,
      });
      toast.success(`Broadcast sent to ${res.recipients} users`);
      setBroadcastSubject("");
      setBroadcastMessage("");
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error sending broadcast");
    }
  };

  if (!user || user.level !== "admin") {
    return (
      <div className="beat-container">
        <div className="beat-card p-8 rounded-lg text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold text-newspaper-900 mb-2">Access Denied</h2>
          <p className="text-newspaper-600">You do not have administrator permissions to access this page.</p>
          <Link to="/" className="beat-button mt-4 inline-block">
            Return to The Front Page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="beat-container">
      {/* Header */}
      <div className="beat-card p-8 mb-8 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-newspaper-900 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-newspaper-900">Admin Panel</h1>
              <p className="text-newspaper-600 mt-1">Manage users, posts, and platform settings</p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm text-newspaper-600">Administrator:</p>
            <p className="font-semibold text-newspaper-900">{user.username}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="beat-card p-6 rounded-lg">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-newspaper-900">{stats.total_users}</p>
              <p className="text-newspaper-600 text-sm">Total Users</p>
            </div>
          </div>
        </div>

        <div className="beat-card p-6 rounded-lg">
          <div className="flex items-center">
            <FileX className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-newspaper-900">{stats.active_posts}</p>
              <p className="text-newspaper-600 text-sm">Active Posts</p>
            </div>
          </div>
        </div>

        <div className="beat-card p-6 rounded-lg">
          <div className="flex items-center">
            <UserX className="w-8 h-8 text-red-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-newspaper-900">{stats.banned_users}</p>
              <p className="text-newspaper-600 text-sm">Banned Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Maintenance Tools */}
      <div className="beat-card p-6 rounded-lg mb-8">
        <h3 className="text-lg font-semibold text-newspaper-900 mb-4">Maintenance Tools</h3>
        <p className="text-newspaper-600 mb-4">Utilities to clean up duplicate notifications.</p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={async () => {
              try {
                const res = await adminService.cleanupWeekly();
                toast.success(`${res.message}: ${res.deleted}`);
              } catch (error: any) {
                toast.error(error.response?.data?.detail || "Error cleaning Weekly Mojo notifications");
              }
            }}
            className="px-4 py-2 bg-newspaper-900 text-white rounded-lg hover:bg-newspaper-800"
          >
            Cleanup Weekly Mojo
          </button>
          <button
            onClick={async () => {
              try {
                const res = await adminService.cleanupRatings();
                toast.success(`${res.message}: ${res.deleted}`);
              } catch (error: any) {
                toast.error(error.response?.data?.detail || "Error cleaning Ratings notifications");
              }
            }}
            className="px-4 py-2 bg-newspaper-900 text-white rounded-lg hover:bg-newspaper-800"
          >
            Cleanup Ratings
          </button>
        </div>
        <div className="mt-6 border-t border-newspaper-200 pt-6">
          <h4 className="text-md font-semibold text-newspaper-900 mb-3">Broadcast Notification</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Subject"
              value={broadcastSubject}
              onChange={(e) => setBroadcastSubject(e.target.value)}
              className="px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Message"
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              className="px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent md:col-span-2"
            />
            <button
              onClick={handleSendBroadcast}
              className="px-4 py-2 bg-newspaper-900 text-white rounded-lg hover:bg-newspaper-800"
            >
              Send to all active users
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="beat-card p-0 mb-8 rounded-lg">
        <div className="border-b border-newspaper-300">
          <nav className="flex">
            {[
              { key: "users", label: "Users", icon: Users },
              { key: "posts", label: "Posts", icon: FileX },
              { key: "banned", label: "Banned", icon: Ban },
              { key: "contact", label: "Contact", icon: Mail },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === key
                    ? "border-newspaper-900 text-newspaper-900"
                    : "border-transparent text-newspaper-600 hover:text-newspaper-900"
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Users Tab */}
          {activeTab === "users" && (
            <div>
              {/* User Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-newspaper-500 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by username or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <select
                  value={userLevelFilter}
                  onChange={(e) => setUserLevelFilter(e.target.value)}
                  className="px-4 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                >
                  <option value="">All levels</option>
                  <option value="user">Users</option>
                  <option value="admin">Administrators</option>
                </select>
              </div>

              {/* Users List */}
              <div className="space-y-4">
                {users.map((userData) => (
                  <div
                    key={userData.id}
                    className="border border-newspaper-200 rounded-lg p-4 hover:bg-newspaper-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                          <div>
                            <h4 className="font-semibold text-newspaper-900">{userData.username}</h4>
                            <p className="text-sm text-newspaper-600">{userData.email}</p>
                          </div>

                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              userData.level === "admin"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {userData.level === "admin" ? "Admin" : "User"}
                          </span>
                        </div>

                        <div className="mt-2 text-sm text-newspaper-600">
                          <span className="mr-4">{userData.posts_count} posts</span>
                          <span className="mr-4">{userData.followers_count} followers</span>
                          <span className="mr-4">{Math.round(userData.mojo)} mojo</span>
                          {userData.last_login && (
                            <span>
                              Last login: {format(new Date(userData.last_login), "dd/MM/yyyy HH:mm", { locale: enGB })}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {/* Promote/Demote Button */}
                        {userData.id !== user?.id && (
                          <button
                            onClick={() =>
                              handlePromoteUser(userData.id, userData.level === "admin" ? "user" : "admin")
                            }
                            className={`px-3 py-1 text-xs rounded-lg ${
                              userData.level === "admin"
                                ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                : "bg-purple-200 text-purple-700 hover:bg-purple-300"
                            }`}
                          >
                            {userData.level === "admin" ? (
                              <>
                                <UserMinus className="w-3 h-3 mr-1 inline" />
                                Remove Admin
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-3 h-3 mr-1 inline" />
                                Make Admin
                              </>
                            )}
                          </button>
                        )}

                        {/* Ban Button */}
                        {userData.id !== user?.id && userData.level !== "admin" && (
                          <button
                            onClick={() => {
                              setSelectedUser(userData);
                              setShowBanModal(true);
                            }}
                            className="px-3 py-1 text-xs bg-red-200 text-red-700 rounded-lg hover:bg-red-300"
                          >
                            <Ban className="w-3 h-3 mr-1 inline" />
                            Ban
                          </button>
                        )}

                        {/* Send Notification Button */}
                        <button
                          onClick={() => {
                            setSelectedUser(userData);
                            setShowNotificationModal(true);
                          }}
                          className="px-3 py-1 text-xs bg-blue-200 text-blue-700 rounded-lg hover:bg-blue-300"
                        >
                          <Mail className="w-3 h-3 mr-1 inline" />
                          Notify
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Posts Tab */}
          {activeTab === "posts" && (
            <div>
              {/* Post Filters */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-newspaper-500 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by title or author..."
                    value={postSearch}
                    onChange={(e) => setPostSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Posts List */}
              <div className="space-y-4">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className="border border-newspaper-200 rounded-lg p-4 hover:bg-newspaper-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Link to={`/post/${post.id}`} className="block">
                          <h4 className="font-semibold text-newspaper-900 hover:text-newspaper-700 mb-2">
                            {post.title}
                          </h4>
                        </Link>

                        <p className="text-sm text-newspaper-600 mb-2 line-clamp-2">
                          {post.content.length > 150 ? `${post.content.substring(0, 150)}...` : post.content}
                        </p>

                        <div className="text-sm text-newspaper-600">
                          <span className="mr-4">By: {post.author_username}</span>
                          <span className="mr-4">{post.visits} views</span>
                          <span className="mr-4">{post.ratings_count} ratings</span>
                          <span>{format(new Date(post.created_at), "dd/MM/yyyy", { locale: enGB })}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setSelectedPost(post);
                          setShowDeletePostModal(true);
                        }}
                        className="px-3 py-1 text-xs bg-red-200 text-red-700 rounded-lg hover:bg-red-300 ml-4"
                      >
                        <Trash2 className="w-3 h-3 mr-1 inline" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Banned Users Tab */}
          {activeTab === "banned" && (
            <div>
              <div className="space-y-4">
                {bannedUsers.map((bannedUser) => (
                  <div key={bannedUser.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                          <div>
                            <h4 className="font-semibold text-newspaper-900">{bannedUser.username}</h4>
                            <p className="text-sm text-newspaper-600">{bannedUser.email}</p>
                          </div>

                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              bannedUser.ban_permanent ? "bg-red-100 text-red-800" : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {bannedUser.ban_permanent ? "Ban Permanente" : "Ban Temporal"}
                          </span>
                        </div>

                        <div className="mt-2 text-sm text-newspaper-700">
                          <p className="mb-1">
                            <strong>Reason:</strong> {bannedUser.ban_reason || "No reason specified"}
                          </p>
                          <p className="mb-1">
                            <strong>Time remaining:</strong> {bannedUser.time_remaining}
                          </p>
                          {bannedUser.ban_until && (
                            <p>
                              <strong>Ends:</strong>{" "}
                              {format(new Date(bannedUser.ban_until), "dd/MM/yyyy HH:mm", { locale: enGB })}
                            </p>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleUnbanUser(bannedUser.id)}
                        className="px-3 py-1 text-xs bg-green-200 text-green-700 rounded-lg hover:bg-green-300"
                      >
                        <CheckCircle className="w-3 h-3 mr-1 inline" />
                        Unban
                      </button>
                    </div>
                  </div>
                ))}

                {bannedUsers.length === 0 && (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p className="text-newspaper-600">There are currently no banned users</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === "contact" && (
            <div>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-newspaper-500 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Buscar por email, username, asunto o mensaje..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {contactMessages.map((m: any) => (
                  <div key={m.id} className="border border-newspaper-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-sm text-newspaper-600 mb-1">
                          <span className="mr-3">From: {m.sender_username || m.sender_email}</span>
                          {m.subject && <span className="mr-3">Subject: {m.subject}</span>}
                          <span className="mr-3">Status: {m.status}</span>
                          {m.created_at && (
                            <span>{format(new Date(m.created_at), "dd/MM/yyyy HH:mm", { locale: enGB })}</span>
                          )}
                        </div>
                        <p className="text-newspaper-900 mb-3">{m.message}</p>
                        {m.reply_message && (
                          <div className="bg-newspaper-50 p-3 rounded">
                            <p className="text-sm text-newspaper-700">Reply: {m.reply_message}</p>
                            {m.replied_at && (
                              <p className="text-xs text-newspaper-500 mt-1">
                                {format(new Date(m.replied_at), "dd/MM/yyyy HH:mm", { locale: enGB })}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <input
                        type="text"
                        value={replyTargetId === m.id ? replyText : ""}
                        onChange={(e) => {
                          setReplyTargetId(m.id);
                          setReplyText(e.target.value);
                        }}
                        placeholder="Escribe una respuesta..."
                        className="flex-1 px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                      />
                      <button
                        onClick={async () => {
                          if (!replyText.trim()) {
                            toast.error("Escribe una respuesta");
                            return;
                          }
                          try {
                            setReplySendingId(m.id);
                            await contactService.replyMessage(m.id, replyText.trim());
                            toast.success("Respuesta enviada");
                            setReplyText("");
                            setReplyTargetId(null);
                            loadContactMessages();
                          } catch (error: any) {
                            toast.error(error.response?.data?.detail || "Error enviando respuesta");
                          } finally {
                            setReplySendingId(null);
                          }
                        }}
                        className="px-4 py-2 bg-newspaper-900 text-white rounded-lg hover:bg-newspaper-800 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                        disabled={replySendingId === m.id}
                        aria-busy={replySendingId === m.id}
                      >
                        {replySendingId === m.id ? <div className="beat-spinner" /> : "Responder"}
                      </button>
                      <button
                        onClick={() => {
                          setDeleteContactTargetId(m.id);
                          setShowDeleteContactModal(true);
                        }}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                        title="Eliminar"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Eliminar</span>
                      </button>
                    </div>
                  </div>
                ))}
                {contactMessages.length === 0 && <div className="text-center text-newspaper-600">No hay mensajes</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ban User Modal */}
      {showBanModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-newspaper-900 mb-4">Ban User: {selectedUser.username}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-newspaper-700 mb-2">Type of ban</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="temporary"
                        checked={banType === "temporary"}
                        onChange={(e) => setBanType(e.target.value as "temporary")}
                        className="mr-2"
                      />
                      Temporary
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="permanent"
                        checked={banType === "permanent"}
                        onChange={(e) => setBanType(e.target.value as "permanent")}
                        className="mr-2"
                      />
                      Permanent
                    </label>
                  </div>
                </div>

                {banType === "temporary" && (
                  <div>
                    <label className="block text-sm font-medium text-newspaper-700 mb-2">Duration (days)</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={banDuration}
                      onChange={(e) => setBanDuration(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-newspaper-700 mb-2">Reason for ban</label>
                  <textarea
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Explain the reason for the ban..."
                    rows={3}
                    className="w-full px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowBanModal(false);
                    setBanReason("");
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-newspaper-600 hover:text-newspaper-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBanUser}
                  disabled={!banReason.trim()}
                  className="beat-button bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ban User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Post Modal */}
      {showDeletePostModal && selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-newspaper-900 mb-4">Delete Post: {selectedPost.title}</h3>

              <div>
                <label className="block text-sm font-medium text-newspaper-700 mb-2">Reason for deletion</label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Explain why this post is being deleted..."
                  rows={3}
                  className="w-full px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeletePostModal(false);
                    setDeleteReason("");
                    setSelectedPost(null);
                  }}
                  className="px-4 py-2 text-newspaper-600 hover:text-newspaper-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePost}
                  disabled={!deleteReason.trim()}
                  className="beat-button bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete Post
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotificationModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-newspaper-900 mb-4">
                Send Notification to: {selectedUser.username}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-newspaper-700 mb-2">Subject</label>
                  <input
                    type="text"
                    value={notificationSubject}
                    onChange={(e) => setNotificationSubject(e.target.value)}
                    placeholder="Notification subject..."
                    className="w-full px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-newspaper-700 mb-2">Message</label>
                  <textarea
                    value={notificationMessage}
                    onChange={(e) => setNotificationMessage(e.target.value)}
                    placeholder="Write your message..."
                    rows={4}
                    className="w-full px-3 py-2 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowNotificationModal(false);
                    setNotificationSubject("");
                    setNotificationMessage("");
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-newspaper-600 hover:text-newspaper-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  disabled={!notificationSubject.trim() || !notificationMessage.trim()}
                  className="beat-button disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send Notification
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Contact Message Modal */}
      {showDeleteContactModal && deleteContactTargetId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-newspaper-900 mb-4">Confirm deletion</h3>
              <p className="text-newspaper-700">
                Are you sure you want to delete this contact message? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteContactModal(false);
                    setDeleteContactTargetId(null);
                  }}
                  className="px-4 py-2 text-newspaper-600 hover:text-newspaper-800"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!deleteContactTargetId) return;
                    try {
                      setDeleteContactLoading(true);
                      await contactService.deleteMessage(deleteContactTargetId);
                      toast.success("Message deleted");
                      setShowDeleteContactModal(false);
                      setDeleteContactTargetId(null);
                      loadContactMessages();
                    } catch (error: any) {
                      toast.error(error.response?.data?.detail || "Error deleting message");
                    } finally {
                      setDeleteContactLoading(false);
                    }
                  }}
                  disabled={deleteContactLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleteContactLoading ? <div className="beat-spinner" /> : <Trash2 className="w-4 h-4" />}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
